import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Download, Eye, FileText, Globe, Clock, CheckCircle, XCircle } from 'lucide-react';
import LoginForm from './LoginForm';

interface VideoRecord {
  id: string;
  original_filename: string;
  status?: 'pending' | 'processing' | 'completed' | 'failed' | 'full_processing';
  created_at: string;
  preview: {
    status: 'not_started' | 'pending' | 'processing' | 'completed' | 'failed';
    s3_srt_url?: string;
    cached_translations?: Record<string, string>; // { "vi": "url", "en": "url", ... }
  };
  full: {
    status: 'not_started' | 'pending' | 'processing' | 'completed' | 'failed';
    s3_srt_url?: string;
    translation_count?: number;
    cached_translations?: Record<string, string>; // { "vi": "url", "en": "url", ... }
  };
  user_email?: string;
  firebase_uid?: string;
}

interface VideoHistoryProps {
  onClose?: () => void;
}

const VideoHistory: React.FC<VideoHistoryProps> = ({ onClose }) => {
  const { user, firebaseUid } = useAuth();
  const [videos, setVideos] = useState<VideoRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (firebaseUid) {
      fetchUserVideos();
    } else {
      setLoading(false);
    }
  }, [firebaseUid]);

  const fetchUserVideos = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${import.meta.env.VITE_CF_WORKER_URL}/api/user/videos`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Firebase-UID': firebaseUid || '',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch videos');
      }

      const data = await response.json();
      setVideos(data.videos || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };



  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'processing':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!user) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50">
        <div className="relative">
          <LoginForm 
            onSuccess={() => onClose?.()} 
            onClose={() => onClose?.()} 
          />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50">
        <div className="relative">
          <div className="w-full max-w-4xl mx-auto bg-white rounded-lg shadow-md p-8" style={{ marginTop: '600px', width: '80vw', maxWidth: '1200px' }}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Video History</h2>
              {onClose && (
                <button
                  onClick={onClose}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                >
                  ✕
                </button>
              )}
            </div>
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading your videos...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50">
        <div className="relative">
          <div className="w-full max-w-4xl mx-auto bg-white rounded-lg shadow-md p-8" style={{ marginTop: '600px', width: '80vw', maxWidth: '1200px' }}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Video History</h2>
              {onClose && (
                <button
                  onClick={onClose}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                >
                  ✕
                </button>
              )}
            </div>
            <div className="text-center">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-600">Error: {error}</p>
                <button
                  onClick={fetchUserVideos}
                  className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="relative">
        <div className="w-full max-w-6xl mx-auto bg-white rounded-lg shadow-md p-8" style={{ marginTop: '600px', width: '90vw', maxWidth: '1400px' }}>
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Video History</h2>
              <p className="text-gray-600">View and download your converted videos</p>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              >
                ✕
              </button>
            )}
          </div>

          {videos.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No videos found</h3>
              <p className="text-gray-600">You haven't converted any videos yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      File Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Preview SRT
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Full SRT
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Translations
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {videos.map((video, index) => (
                    <tr key={video.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
                          {video.original_filename}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(video.status || 'pending')}
                          <span className="ml-2 text-sm text-gray-900 capitalize">
                            {video.status || 'pending'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {video.preview.s3_srt_url ? (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => downloadFile(video.preview.s3_srt_url!, `${video.original_filename}_preview.srt`)}
                              className="text-blue-600 hover:text-blue-800"
                              title="Download Preview SRT"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => window.open(video.preview.s3_srt_url, '_blank')}
                              className="text-green-600 hover:text-green-800"
                              title="View Preview SRT"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">Not available</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {video.full.s3_srt_url ? (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => downloadFile(video.full.s3_srt_url!, `${video.original_filename}_full.srt`)}
                              className="text-blue-600 hover:text-blue-800"
                              title="Download Full SRT"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => window.open(video.full.s3_srt_url, '_blank')}
                              className="text-green-600 hover:text-green-800"
                              title="View Full SRT"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">Not available</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {(() => {
                          // Get translations from both preview and full cached_translations
                          const previewTranslations = video.preview.cached_translations || {};
                          const fullTranslations = video.full.cached_translations || {};
                          const allTranslations = { ...previewTranslations, ...fullTranslations };
                          
                          if (Object.keys(allTranslations).length > 0) {
                            return (
                              <div className="space-y-1">
                                {Object.entries(allTranslations).map(([language, url]) => (
                                  <div key={language} className="flex items-center justify-between bg-gray-50 rounded px-2 py-1">
                                    <span className="text-xs text-gray-600 flex items-center">
                                      <Globe className="w-3 h-3 mr-1" />
                                      {language.toUpperCase()}
                                    </span>
                                    <div className="flex space-x-1">
                                      <button
                                        onClick={() => downloadFile(url, `${video.original_filename}_${language}.srt`)}
                                        className="text-blue-600 hover:text-blue-800"
                                        title={`Download ${language} SRT`}
                                      >
                                        <Download className="w-3 h-3" />
                                      </button>
                                      <button
                                        onClick={() => window.open(url, '_blank')}
                                        className="text-green-600 hover:text-green-800"
                                        title={`View ${language} SRT`}
                                      >
                                        <Eye className="w-3 h-3" />
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            );
                          } else {
                            return <span className="text-gray-400 text-sm">No translations</span>;
                          }
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(video.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoHistory;